/**
 * Dynamic lab navigation menu items grouped by type
 */

import { Divider, List, Tooltip, Typography } from "@mui/material";
import { InfoOutlined, LockOutlined } from "@mui/icons-material";
import { createElement } from "react";
import { useAuth } from "../../utils/useAuth";
import { useLabsWithAccess, resolveLabUrl } from "../../utils/useLabsConfig";
import { getLabIcon } from "../../config/lab-icons.config";
import { DrawerNavMenuItem } from "./DrawerNavMenuItem";

export function LabNavMenuItems({ collapsed }: { collapsed?: boolean }) {
  const { user } = useAuth();
  const labsWithAccess = useLabsWithAccess();

  if (labsWithAccess.length === 0) {
    return null;
  }

  const roleNamespace = window.APP_CONFIG?.roleClaimNamespace;
  const userRoles: string[] = roleNamespace && user ? (user[roleNamespace] as string[]) || [] : [];
  const isAdmin = userRoles.includes("Admin");

  const userLabs = labsWithAccess.filter(
    ({ lab }) => (lab.type === "user" || lab.type === undefined) && lab.url
  );
  const serviceLabs = labsWithAccess.filter(({ lab }) => lab.type === "service" && lab.url);

  return (
    <List disablePadding>
      {userLabs.length > 0 && (
        <>
          {collapsed ? (
            <Divider sx={{ pt: 1.5, pb: 0.5 }} />
          ) : (
            <Divider textAlign="left" sx={{ pt: 1.5, pb: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                User Labs
                <Tooltip
                  title="Labs which authenticate and provide data access on behalf of an individual insight end-user. These applications access user and event data from Insight APIs and therefore operate within its security model. User Labs may eventually make their way into Public Labs."
                  placement="right"
                >
                  <InfoOutlined sx={{ fontSize: 14 }} />
                </Tooltip>
              </Typography>
            </Divider>
          )}
          {userLabs.map(({ lab, hasAccess }) => {
            const IconComponent = getLabIcon(lab.icon);
            const path = lab.open_in_new_tab ? resolveLabUrl(lab.url) : `/labs/${lab.id}`;
            return (
              <DrawerNavMenuItem
                key={lab.id}
                icon={createElement(IconComponent)}
                label={lab.name}
                tooltip={`${lab.name} - ${lab.description}`}
                path={path}
                collapsed={collapsed}
                disabled={!hasAccess}
                disabledTooltip="Requires access"
                openInNewTab={lab.open_in_new_tab}
              />
            );
          })}
        </>
      )}
      {isAdmin && serviceLabs.length > 0 && (
        <>
          {collapsed ? (
            <Divider sx={{ pt: 1.5, pb: 0.5 }} />
          ) : (
            <Divider textAlign="left" sx={{ pt: 1.5, pb: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                Service Labs
                <Tooltip
                  title="Labs that provide cross-user capabilities in support of the Insight system as a whole. These applications are responsible for their own security model. Service Labs are never meant to be accessed by Users and will always stay internal."
                  placement="right"
                >
                  <LockOutlined sx={{ fontSize: 14 }} />
                </Tooltip>
              </Typography>
            </Divider>
          )}
          {serviceLabs.map(({ lab, hasAccess }) => {
            const IconComponent = getLabIcon(lab.icon);
            const path = lab.open_in_new_tab ? resolveLabUrl(lab.url) : `/labs/${lab.id}`;
            return (
              <DrawerNavMenuItem
                key={lab.id}
                icon={createElement(IconComponent)}
                label={lab.name}
                tooltip={`${lab.name} - ${lab.description}`}
                path={path}
                collapsed={collapsed}
                disabled={!hasAccess}
                disabledTooltip="Requires access"
                openInNewTab={lab.open_in_new_tab}
              />
            );
          })}
        </>
      )}
    </List>
  );
}
